import {
  AnalyticsActionPaymentDetailsDebitCreditSelectEvent,
  AnalyticsActionPaymentDetailsPayPalSelectEvent,
  AnalyticsApi,
  analyticsBindingMapping,
  AnalyticsContext,
  AnalyticsDatabinder,
  AnalyticsEvent,
  AnalyticsEventParam,
  AnalyticsEventType,
  AnalyticsPayload,
  AnalyticsScreenViewEvent,
  AnalyticsTechUpdateContextEvent,
  AnalyticsV3,
  createAnalyticsPayload,
  Duration,
  DurationUnit,
  getPayloadForAnalyticsEvent,
  getTechnicalPayloadForAnalyticsEvent,
  IntegrationType,
  PaymentMethod,
  PaymentMethodTypes,
  PluginNotReadyError,
  PurchasableProduct,
  replaceDeprecatedBindingKeyInValue,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import { applyDatabinding } from '@dynamic/ui';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { dset as set } from 'dset';
import { RefObject } from 'react';

import { Item } from '#/client/models';
import {
  CoreAnalyticsEventsConfig,
  IntegrationConfig,
  LegacyAnalyticsEventsConfig,
} from '#/service/config/common.types';
import { MainConfig } from '#/service/config/config.types';
import {
  FeatureAnalyticsProps,
  FeatureArrayData,
} from '#/service/config/features.types';
import { falsyToNull } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

dayjs.extend(duration);

const { Browse, Details, Search, WatchHistory, CancelPage } = ScreenType;

const screenTypeMapping: Record<string, ScreenType> = {
  details: Details,
  search: Search,
  history: WatchHistory,
  cancel: CancelPage,
};

interface HandleScreenViewParams {
  screenTitle?: string;
  screenHeading?: string;
  screenType?: ScreenType;
  item?: Item;
}

interface HandlePaymentButtonClick {
  label: string;
  paymentMethod?: PaymentMethod;
  product?: PurchasableProduct;
  voucherCode?: string;
  discountedPrice?: string;
}

// Helper to get screen title from parameter, then item title, then document title.
export const getScreenTitle = (screenTitle?: string, item?: Item): string =>
  falsyToNull(screenTitle) ?? item?.title ?? document.title;

// Helper to get screen type, either from parameter or by finding it from URL.
export const getScreenType = (screenType?: ScreenType): ScreenType =>
  falsyToNull(screenType) ?? getScreenTypeFromMapping();

export const handleScreenView = ({
  screenTitle,
  screenType,
  screenHeading,
  item,
}: HandleScreenViewParams) => {
  document.dispatchEvent(
    new AnalyticsScreenViewEvent({
      [AnalyticsEventParam.ScreenHeading]: falsyToNull(screenHeading),
      [AnalyticsEventParam.ScreenTitle]: getScreenTitle(screenTitle, item),
      [AnalyticsEventParam.ScreenType]: getScreenType(screenType),
      [AnalyticsEventParam.Title]: item?.title,
      [AnalyticsEventParam.TvShowTitle]: item?.tvShow?.title,
      [AnalyticsEventParam.Subtype]: item?.subtype,
      [AnalyticsEventParam.Extras]: item?.extras,
    })
  );
};

/** Global handler for sending button (submit) clicks within the payment form. */
export const handlePaymentButtonClick = ({
  paymentMethod,
  label,
  product,
  voucherCode,
  discountedPrice,
}: HandlePaymentButtonClick) => {
  const payload = {
    [AnalyticsEventParam.ScreenHeading]: tb('iap.setUpYourPayment'),
    [AnalyticsEventParam.ScreenType]: ScreenType.Purchase,
    [AnalyticsEventParam.ScreenTitle]: getScreenTitle(),
    [AnalyticsEventParam.Label]: falsyToNull(label),
    [AnalyticsEventParam.ProductId]: falsyToNull(product?.id),
    [AnalyticsEventParam.Price]: discountedPrice ?? falsyToNull(product?.price),
    [AnalyticsEventParam.Currency]: falsyToNull(product?.currency),
    [AnalyticsEventParam.SubscriptionPlan]: falsyToNull(product?.title),
    [AnalyticsEventParam.SubscriptionPeriod]: durationToIso8601(
      product?.duration
    ),
    [AnalyticsEventParam.VoucherCode]: falsyToNull(voucherCode),
  };

  if (paymentMethod?.type === PaymentMethodTypes.Paypal) {
    document.dispatchEvent(
      new AnalyticsActionPaymentDetailsPayPalSelectEvent(payload)
    );
  } else if (paymentMethod?.type === PaymentMethodTypes.CreditCard) {
    document.dispatchEvent(
      new AnalyticsActionPaymentDetailsDebitCreditSelectEvent(payload)
    );
  }
};

export const getBoundData: AnalyticsDatabinder = (value, context, pluginId) => {
  const updatedValue = replaceDeprecatedBindingKeyInValue(value);

  return applyDatabinding({
    value: updatedValue,
    context: transformContext(context, pluginId),
  });
};

export const getScreenTypeFromMapping = () => {
  const mappingKey = Object.keys(screenTypeMapping).find((key) =>
    window.location.pathname.includes(key)
  );

  return mappingKey ? screenTypeMapping[mappingKey] : Browse;
};

const reversedBindingMapping = Object.fromEntries(
  Object.entries(analyticsBindingMapping).map(
    ([bindingKey, analyticsEventParam]) => [analyticsEventParam, bindingKey]
  )
);

/**
 * Util for mapping values from analytics context event params to binding keys instead.
 * Exported only for testing purposes.
 *
 * @param context - analytics context using analytics event params as keys
 * @param pluginId - identifier for plugin, needed only for logging
 * @returns mapping between binding keys and values from context
 */
export const transformContext = (
  context: AnalyticsContext | AnalyticsV3.ParamValues,
  pluginId: string
) => {
  return Object.entries(context).reduce(
    (result, [analyticsEventParam, value]) => {
      if (analyticsEventParam.startsWith('tech')) {
        return result;
      }

      const bindingKey = reversedBindingMapping[analyticsEventParam];

      if (bindingKey) {
        set(result, bindingKey, value);
      } else if (value) {
        // all `AnalyticsEventParam`s are added to the context with null value by default.
        // only warn about them if we try to pass an actual value.
        console.warn(
          `${pluginId} - No binding mapping was found for parameter "${analyticsEventParam}".`
        );
      }

      return result;
    },
    {}
  );
};

export const normalizeAnalyticsEvent = <T extends { [key: string]: any }>(
  object: T
): T => {
  // Shallow copying object should be enough
  const _object = { ...object };

  for (const key in _object) {
    _object[key] = falsyToNull(_object[key]) as T[Extract<keyof T, string>];
  }

  return _object;
};

/**
 * Creates payloads for structured analytics events.  Used in legacy.json when analyticsEvents is defined (moved to legacyAnalyticsEvents in web backend to avoid confusion with new analyticsEvents).
 * @param analyticsProps
 * @param incomingEvent
 * @param analyticsEvent
 * @param pluginId
 * @param populatedContext
 */
export const getPayloadsForStructuredEvents = ({
  analyticsProps,
  incomingEvent,
  analyticsEvent,
  pluginId,
  populatedContext,
}: {
  analyticsProps: FeatureAnalyticsProps;
  incomingEvent: AnalyticsEventType;
  analyticsEvent: AnalyticsEvent;
  pluginId: string;
  populatedContext: AnalyticsContext;
}) => {
  const eventsToSend = analyticsProps?.structuredEvents?.[incomingEvent] ?? [];

  return eventsToSend.map((evt) => ({
    ...createAnalyticsPayload({
      config: { ...analyticsProps, properties: evt?.properties },
      event: analyticsEvent, // internal name, ex: screen.view
      context: populatedContext,
      pluginId: pluginId ?? 'Unknown analytics plugin',
      databinder: getBoundData,
      useTypedBindings: true,
    }),
    eventName: evt.event, // outgoing name, ex: 'screenView', 'page_view', etc
  }));
};

/**
 * Creates payloads for unstructured analytics events.  The OLD plugin way of handling analytics events.  Used when a even is triggered from features.analytics.
 * @param analyticsProps
 * @param analyticsEvent
 * @param populatedContext
 * @param pluginId
 */
export const getPayloadsForUnstructuredEvents = ({
  analyticsProps,
  analyticsEvent,
  populatedContext,
  pluginId,
}: {
  analyticsProps: FeatureAnalyticsProps;
  analyticsEvent: AnalyticsEvent;
  populatedContext: AnalyticsContext;
  pluginId: string;
}) => {
  const customPayload = createAnalyticsPayload({
    config: analyticsProps,
    event: analyticsEvent,
    context: populatedContext,
    pluginId,
    databinder: getBoundData,
  });

  return [customPayload];
};

/**
 * Used to handle analytics events for Core integrations (CLIENT_ANALYTICS) that are defined in analyticsEvents in runtime configuration.
 * @param incomingEvent
 * @param coreAnalyticsEvents
 * @param integrations
 * @param configs
 * @param context
 * @param isTechnical
 */
export const handleIntegrationAnalyticsEvents = (
  incomingEvent: AnalyticsEventType,
  coreAnalyticsEvents: CoreAnalyticsEventsConfig[],
  integrations: RefObject<AnalyticsV3.AnalyticsApi<object>>[],
  configs: FeatureArrayData<FeatureAnalyticsProps>,
  context: AnalyticsContext,
  isTechnical: Boolean
) => {
  // Step 1 - Find matching incoming event from analyticsEvents in config
  if (isTechnical) {
    integrations.forEach((integrationApi, index) => {
      bufferAnalyticsEvents(
        [getTechnicalPayloadForAnalyticsEvent(context)],
        integrationApi,
        configs[index].key,
        incomingEvent
      );
    });
  }
  const matchingEvent = coreAnalyticsEvents.find(
    ({ event }) => incomingEvent === event
  );

  if (!matchingEvent) {
    bufferLoop();

    return;
  }

  // Step 2 - Loop through publishTo value
  matchingEvent.publishTo.forEach(({ event, integration, properties }) => {
    // Step 3 - Find matching integration from CLIENT_ANALYTICS
    const index = configs.findIndex(({ key }) => key === integration);
    const config = configs[index];
    const integrationInstance = integrations[index];

    if (!integrationInstance?.current || !config) {
      return;
    }
    // Step 4 - Prepare the payload with databinding and flattening the lower level type, keeping the groupProperties as is
    const payload = getPayloadForAnalyticsEvent({
      context,
      pluginId: config.key,
      eventName: event,
      properties,
      databinder: getBoundData,
    });

    // Step 5 - Dispatch the event with the prepared payload to integration
    bufferAnalyticsEvents(
      [payload],
      integrationInstance,
      config.key,
      incomingEvent
    );
  });
  bufferLoop();
};

export const TIMEOUT = 1000;

let timeoutId: number;

type EventCallback = () => void;
let eventBuffer: EventCallback[] = [];

// Helper function to buffer analytics events - extracted to reduce nesting
export const bufferAnalyticsEvents = (
  payloadsToSend: (
    | AnalyticsV3.AnalyticsEventPayloadBound<Partial<object>>
    | AnalyticsPayload
  )[],
  apiRef: RefObject<AnalyticsApi | AnalyticsV3.AnalyticsApi<object>>,
  pluginId: string | undefined,
  event: AnalyticsEventType
): void => {
  payloadsToSend.forEach((customPayload) => {
    const bufferedEvent = () => {
      if (!apiRef.current) {
        throw new PluginNotReadyError(pluginId ?? '');
      }

      // `commonContext` is a required field on `AnalyticsPayload` (legacy v1 API) but absent from
      // `AnalyticsV3.AnalyticsEventPayloadBound` (v3 API). It is the only stable structural discriminant between the
      // two payload shapes. We are doing the same thing in both cases, we just need to convince TypeScript.
      if ('commonContext' in customPayload) {
        apiRef.current.sendEvent(event, normalizeAnalyticsEvent(customPayload));
      } else {
        (apiRef.current as AnalyticsV3.AnalyticsApi<object>).sendEvent(
          event,
          normalizeAnalyticsEvent(customPayload)
        );
      }
    };
    eventBuffer.push(bufferedEvent);
  });
};

// loops through `eventBuffer` and sends events
// re-sends events that were rejected with `PluginNotReadyError`
export const bufferLoop = () => {
  clearTimeout(timeoutId);

  eventBuffer = eventBuffer.reduce(
    (queue: EventCallback[], sendEvent: EventCallback) => {
      try {
        sendEvent();
      } catch (error) {
        if (error instanceof Error && error.name === 'PluginNotReadyError') {
          queue.push(sendEvent);
        } else {
          console.error(error);
        }
      }

      return queue;
    },
    []
  );

  if (eventBuffer.length) {
    timeoutId = window.setTimeout(bufferLoop, TIMEOUT);
  }
};

/**
 * Splits loaded analytics plugins and integrations by checking the `src` of the plugin and if it exists.  Integrations do not have a corresponding plugin.
 * @param apiRefs
 * @param configs
 */
export const splitLoadedAnalyticsPluginsAndIntegrations = (
  apiRefs: RefObject<AnalyticsApi | AnalyticsV3.AnalyticsApi<object>>[],
  configs: FeatureArrayData<FeatureAnalyticsProps>
) => {
  const integrationApiRefs: RefObject<AnalyticsV3.AnalyticsApi<object>>[] = [];
  const integrationConfigs: FeatureArrayData<FeatureAnalyticsProps> = [];
  const pluginApiRefs: RefObject<AnalyticsApi>[] = [];
  const pluginConfigs: FeatureArrayData<FeatureAnalyticsProps> = [];

  // Easiest way to differentiate loaded plugins and integrations is by checking the src of the plugin.
  // This entire function be removed once plugins are no longer used, and simply return all as integrations api and configs.
  configs.forEach(({ plugin }, index) => {
    if (plugin?.src?.includes('one-plugin-analytics')) {
      pluginApiRefs.push(apiRefs[index]);
      pluginConfigs.push(configs[index]);
    } else {
      // key has to be an integration.
      integrationApiRefs.push(
        apiRefs[index] as RefObject<AnalyticsV3.AnalyticsApi<object>>
      );
      integrationConfigs.push(configs[index]);
    }
  });

  return {
    integrationApiRefs,
    integrationConfigs,
    pluginApiRefs,
    pluginConfigs,
  };
};

export const handleLegacyAnalyticsEvents = (
  incomingEvent: AnalyticsEventType,
  pluginApiRefs: RefObject<AnalyticsApi>[],
  pluginConfigs: FeatureArrayData<FeatureAnalyticsProps>,
  populatedContext: AnalyticsContext,
  analyticsEvent: AnalyticsEvent,
  isTechnical: Boolean
) => {
  // sending the event to all plugins that are configured to receive it
  pluginApiRefs.forEach((apiRef, index) => {
    const analyticsProps = pluginConfigs[index].properties;
    const isS3Config = Boolean(analyticsProps.events?.[incomingEvent]);
    const isCoreConfig = Boolean(
      analyticsProps.structuredEvents?.[incomingEvent]
    );
    const isConfigured = isS3Config || isCoreConfig;

    // ignore non-tech event if no plugin is configured to receive it
    if (!isConfigured && !isTechnical) {
      return;
    }

    // create the payloads
    const pluginId = pluginConfigs[index].key;
    const isStructured =
      analyticsProps?.structuredEvents?.[incomingEvent]?.length;
    const payloadsToSend = isStructured
      ? getPayloadsForStructuredEvents({
          analyticsProps,
          analyticsEvent,
          populatedContext,
          pluginId,
          incomingEvent,
        })
      : getPayloadsForUnstructuredEvents({
          analyticsProps,
          analyticsEvent,
          populatedContext,
          pluginId,
        });

    // push all the outgoing events to the buffer
    bufferAnalyticsEvents(payloadsToSend, apiRef, pluginId, incomingEvent);

    // try to send everything in the buffer
    bufferLoop();
  });
};

// merge analytics configs coming from S3 features and Core integrations
export const mergeAnalyticsFeatureWithIntegrations = (
  config: MainConfig
): FeatureArrayData<FeatureAnalyticsProps> => {
  // getting event params from Core analyticsEvents
  const analyticsEventsByVendor = Object.keys(
    config.legacyAnalyticsEvents ?? {}
  ).reduce((acc: LegacyAnalyticsEventsConfig, eventName) => {
    const vendors = Object.keys(config.legacyAnalyticsEvents[eventName]);
    vendors.forEach((vendor) => {
      acc[vendor] ??= {};
      acc[vendor][eventName] = config.legacyAnalyticsEvents[eventName][
        vendor
      ]?.map((event) => ({
        ...event,
        coreEventName: eventName,
        ...config.integrations[vendor]?.configuration,
      }));
    });

    return acc;
  }, {});
  // getting analytics integrations from Core integrations
  const analyticsPluginIntegrationsMap = Object.keys(config.integrations)
    .filter((integration) => {
      const isAnalytics = config.integrations[
        integration
      ]?.activeForFeatures?.includes(IntegrationType.CLIENT_ANALYTICS);
      const hasPluginMapping = integration in (config.pluginIntegrations ?? {});

      return isAnalytics && hasPluginMapping;
    })
    .map((integration) => {
      const plugin = config.pluginIntegrations[integration] ?? {};

      return {
        key: integration,
        plugin: {
          ...plugin,
          // rename `source` to `src` to match the expected property name
          src: plugin.source,
        },
        enabled: true,
        properties: {
          ...config.integrations[integration],
          structuredEvents: analyticsEventsByVendor[integration],
        },
      };
    }, []);

  return [
    ...(config?.features?.analytics || []),
    ...analyticsPluginIntegrationsMap,
  ];
};

export const generateIntegrationAnalyticsEvents = (
  bundleId: string,
  integration: IntegrationConfig,
  analyticsEvents: LegacyAnalyticsEventsConfig
) => {
  if (
    !integration.activeForFeatures.includes(IntegrationType.CLIENT_ANALYTICS)
  ) {
    return;
  }

  // intentionally not the `StructuredEvent` type in `features.types.ts`
  const structuredEvents: Record<string, Record<string, unknown>> = {};

  Object.keys(analyticsEvents).forEach((eventName) => {
    const bundleEvent = analyticsEvents[eventName][bundleId];
    structuredEvents[eventName] = {
      ...bundleEvent,
      coreEventName: eventName,
      ...integration.configuration,
    };
  });

  return {
    structuredEvents,
    // This addition of configuration here is only used for GA4 and should be removed with: https://accedobroadband.jira.com/browse/ONEWEB-2885
    configuration: integration.configuration,
  };
};

/**
 * Converts a duration object to an ISO 8601 duration string.
 * @param duration - The duration object containing count and unit if we have subscription, otherwise it will be undefined.
 * @returns The ISO 8601 duration string or null if the unit is not supported.
 * @see https://en.wikipedia.org/wiki/ISO_8601 for ISO 8601 documentation.
 */
export const durationToIso8601 = (duration?: Duration) => {
  if (!duration) {
    return null;
  }

  const { count, unit } = duration;

  // Following formats cannot be transformed into ISO 8601 duration format because they are not supported.
  if (
    unit === DurationUnit.Forever ||
    unit === DurationUnit.Season ||
    unit === DurationUnit.Unknown
  ) {
    return null;
  }

  const iso8601 = dayjs.duration(count, unit.toLowerCase()).toISOString();

  // P0D means no duration, so we return null in that case instead. This can happen if the count is 0 or unit is not valid
  return iso8601 === 'P0D' ? null : iso8601;
};

export const removeSaveCampaignFromAnalyticsContext = () => {
  document.dispatchEvent(
    new AnalyticsTechUpdateContextEvent({
      [AnalyticsEventParam.SubscriptionId]: undefined,
      [AnalyticsEventParam.OfferName]: undefined,
    })
  );
};
